import { Component, OnInit } from '@angular/core';
import { combineLatest, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { OverlayService } from 'src/app/services/overlay.service';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { ContractService } from 'src/app/services/contract.service';
import { AttachmentService } from 'src/app/services/attachment.service';
import { EnvironmentService } from 'src/app/services/environment.service';
import { ActivatedRoute, Router } from '@angular/router';
import { ImageGalleryComponent } from 'src/app/components/shared/image-gallery/image-gallery.component';
import { MatDialog } from '@angular/material/dialog';

import {
  DecoupledEditor,
  AccessibilityHelp,
  Alignment,
  AutoLink,
  Autosave,
  BalloonToolbar,
  BlockQuote,
  Bold,
  Essentials,
  FontBackgroundColor,
  FontColor,
  FontFamily,
  FontSize,
  GeneralHtmlSupport,
  Heading,
  Highlight,
  HorizontalLine,
  Indent,
  IndentBlock,
  Italic,
  Link,
  List,
  Paragraph,
  RemoveFormat,
  SelectAll,
  SpecialCharacters,
  Strikethrough,
  Underline,
  Undo,
  type EditorConfig,
  ClassicEditor,
  Image,
  ImageBlock,
  ImageInline,
  ImageInsert,
  ImageInsertViaUrl,
  ImageResize,
  ImageStyle,
  ImageToolbar,
  ImageUpload,
  AutoImage,
  SourceEditing,
  ShowBlocks
} from 'ckeditor5';

import translations from 'ckeditor5/translations/de.js';



/**
 * The Options used for sorting
 */
interface SortOption {
  /**
   * The Value of the Filteroption
   */
  value: string;
  /**
   * The viewValue (visible to the User) of the Filteroption
   */
  viewValue: string;
  /**
   * Is the  Filteroption disabled
   */
  state?: boolean;
}
@Component({
  selector: 'app-edit-advertising-box',
  templateUrl: './edit-advertising-box.component.html',
  styleUrls: ['./edit-advertising-box.component.scss']
})
export class EditAdvertisingBoxComponent implements OnInit {
  files: File[] = [];
  addForm: UntypedFormGroup;
  finishedLoading = true;
  intoBoxes = [];
  ReturnintoBoxes = [];
  currentBox;
  changeImage: boolean = false;
  showAsHtml: boolean = false;
  imageProperties = [
    { key: 'logo', value: null, hint: 'Format: 800 x 500, Typ: .png', hasChanged: false },
  ];

  public Editor = ClassicEditor;
  public config = {
    toolbar: {
      items: [
        'undo',
        'redo',
        '|',
        'heading',
        '|',
        'sourceEditing',
        'showBlocks',
        '|',
        'bold',
        'italic',
        'underline',
        'strikethrough',
        '|',
        'link',
        'alignment',
        'insertImage',
        '|',
        'bulletedList',
        'numberedList',
        'outdent',
        'indent'
      ],
      shouldNotGroupWhenFull: true
    },
    plugins: [
      AccessibilityHelp,
      Alignment,
      AutoLink,
      AutoImage,
      Autosave,
      BalloonToolbar,
      BlockQuote,
      Bold,
      Essentials,
      FontBackgroundColor,
      FontColor,
      FontFamily,
      FontSize,
      GeneralHtmlSupport,
      Heading,
      Highlight,
      HorizontalLine,
      Indent,
      IndentBlock,
      Italic,
      Link,
      Image,
      ImageBlock,
      ImageInline,
      ImageInsert,
      ImageInsertViaUrl,
      ImageResize,
      ImageStyle,
      ImageToolbar,
      ImageUpload,
      Paragraph,
      RemoveFormat,
      SelectAll,
      SpecialCharacters,
      Strikethrough,
      Underline,
      Undo,
      List,
      ShowBlocks,
      SourceEditing,
    ],
    heading: {
      options: [
        {
          model: 'paragraph',
          title: 'Paragraph',
          class: 'ck-heading_paragraph'
        },
        {
          model: 'heading1',
          view: 'h1',
          title: 'Überschrift 1',
          class: 'ck-heading_heading1'
        },
        {
          model: 'heading2',
          view: 'h2',
          title: 'Überschrift 2',
          class: 'ck-heading_heading2'
        },
        {
          model: 'heading3',
          view: 'h3',
          title: 'Überschrift 3',
          class: 'ck-heading_heading3'
        },
        {
          model: 'heading4',
          view: 'h4',
          title: 'Überschrift 4',
          class: 'ck-heading_heading4'
        },        {
          model: 'heading5',
          view: 'h5',
          title: 'Überschrift 5',
          class: 'ck-heading_heading5'
        }, 
        {
          model: 'heading6',
          view: 'h6',
          title: 'Überschrift 6',
          class: 'ck-heading_heading6'
        },  
      ]
    },
    htmlSupport: {
      allow: [
        {
          name: /^.*$/,
          styles: true,
          attributes: true,
          classes: true
        }
      ]
    },
    language: 'de',
    link: {
      addTargetToExternalLinks: true,
      defaultProtocol: 'https://',
      decorators: {
        toggleDownloadable: {
          mode: 'manual',
          label: 'Downloadable',
          attributes: {
            download: 'file'
          }
        }
      }
    },
    image: {
      toolbar: ['imageTextAlternative', '|', 'imageStyle:inline', 'imageStyle:wrapText', 'imageStyle:breakText', '|', 'resizeImage']
    },
    placeholder: '',
    translations: [translations]
  }

  public sortOptions = [
    { value: "component_web_intro_landing_page", viewValue: "Startseitengestaltung Web" }
  ];

  public boxTypes = [
    { value: "NONE", viewValue: "Bild nicht anzeigen" },
    { value: "LEFT", viewValue: "Links vom Text" },
    { value: "RIGHT", viewValue: "Rechts vom Text" }
  ];

  public states = [
    { value: "DRAFT", viewValue: "Entwurf" },
    { value: "PUBLISHED", viewValue: "Veröffentlicht" }
  ];

  constructor(
    private route: ActivatedRoute,
    private overlayService: OverlayService,
    private environmentService: EnvironmentService,
    private fb: UntypedFormBuilder,
    private contractService: ContractService,
    private attachmentService: AttachmentService,
    private dialog: MatDialog,
    private router: Router
  ) {
    this.addForm = this.fb.group({
      image: [''],
      imageAlignMent: ["LEFT", [Validators.required]],
      state: ['DRAFT', [Validators.required]],
      text: ['', Validators.required]
    })

    combineLatest([
      this.route.params,
      this.contractService.component_web_intro_landing_page$
    ]).pipe(
      map(([params, data]: any) => {
        this.intoBoxes = JSON.parse(String(data));
        console.log(this.intoBoxes)
        this.intoBoxes.forEach((element) => {
          if (element[0] == params['id']) {
            this.currentBox = element;
            combineLatest([
              this.attachmentService.getInfoDataFile(element[0]).pipe(catchError(error => of(error))),
              this.attachmentService.getInfoData(element[1]).pipe(catchError(error => of(error)))
            ]).pipe(
              map(([data, data1]: any) => {
                console.log(data, data1)
                this.addForm.patchValue({
                  image: data1,
                  imageAlignMent: element[2],
                  state: element[3],
                  text: data
                })
              })).subscribe({
                next: res => {
                  console.log(res)
                },
                error: err => {
                  console.log(err)
                }
              })
          }
        })
      })).subscribe(() => {
        this.finishedLoading = true;
      })
      
  }

  ngOnInit() {

  }



  updateAdvertsing() {
    this.addForm.markAllAsTouched();
    if (this.addForm.invalid) {
      return;
    }

    this.intoBoxes.forEach((element, index) => {
      if (element[0] == this.currentBox[0]) {
        this.intoBoxes[index][1] = this.addForm.getRawValue().image.publicId;
        this.intoBoxes[index][2] = this.addForm.getRawValue().imageAlignMent;
        this.intoBoxes[index][3] = this.addForm.getRawValue().state;
        this.attachmentService.getHTMLAttachment(this.intoBoxes[index][0]).subscribe(val => {
          let t = {
            "clientRef": val.clientRef,
            "publicId": val.publicId,
            "originalFilename": val.originalFilename,
            "type": "text/html",
            "content": this.addForm.getRawValue().text
          }
          this.attachmentService.updateHTMLAttachment(t).pipe(
            switchMap(val => {
              this.intoBoxes[index][0] = val.publicId;
              return this.contractService.updateComponent_web_intro_landing_page(JSON.stringify(this.intoBoxes))
            })
          ).subscribe({
            next: (res: any) => {
              console.log(res)
              this.overlayService.openSnackBar("Werbeflächen erfolgreich bearbeitet!");
              this.router.navigate(['caseworker', 'advertisingbox', 'edit', this.intoBoxes[index][0]]) //"94eb65a3-461d-4dd0-ac0b-0e3b12223134"
            }
          })

        })
      }
    })
  }


  onSelect(event) {
    this.files.push(...event.addedFiles);
    this.attachmentService.createAttachment(this.environmentService.getClientId(), this.files[0]).subscribe(attachment => {
      this.addForm.patchValue({
        image: attachment
      });
      this.toogleImageUpload();
    })
  }

  onRemove(event) {
    this.files.splice(this.files.indexOf(event), 1);
  }
  openImageGallery(property) {
    const dialogRef = this.dialog.open(ImageGalleryComponent);
    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.addForm.patchValue({
          image: result
        })
        property.value = result.url;
        property.hasChanged = true;
        property.newValue = `${result.clientRef}/attachment/${result.publicId}`;
      }
    });
  }

  toogleImageUpload() {
    this.changeImage = !this.changeImage;
  }
}
